import React, {
  useState, useEffect, useCallback, useRef, useMemo,
} from 'react';
import axios from 'axios';
import {
  Table, Form, Input, Checkbox, Switch, Popconfirm, Empty, Spin, Tag, Select,
} from 'antd';
import { Plus } from '@phosphor-icons/react';
import { Modal, message } from './antd';
import Typography from '../../components/Typography';
import Button from '../../components/Button';
import csrfHeaders from '../../utils/csrfHeaders';
import { captureAnalytics } from '../../actions/preload';

const ENDPOINT = '/api/v1/portal/team_members';

const errorMessage = (error, fallback) => (
  error?.response?.data?.errors?.[0]
  || error?.response?.data?.error
  || fallback
);

// Admins first, then alphabetical by name within each group. Applied when the
// list loads from the server; inline edits update rows in place without
// re-sorting, so toggling admin doesn't yank a row across pages mid-edit.
const sortMembers = (list) => [...list].sort((a, b) => {
  if (Boolean(a.is_admin) !== Boolean(b.is_admin)) return a.is_admin ? -1 : 1;
  return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
});

// The server rejects invites outside the org's email domains, so the invite
// form fixes the @domain part and only asks for the local part. Domains come
// from the auth payload via localStorage (same source AddUsers uses); stored
// either as a JSON array or a comma-joined string depending on the writer.
const readOrgDomains = () => {
  try {
    const raw = localStorage.getItem('allowed_user_domains');
    if (!raw) return [];
    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = raw.split(',');
    }
    const list = Array.isArray(parsed) ? parsed : [parsed];
    return list.map((d) => (d || '').toString().trim().toLowerCase()).filter(Boolean);
  } catch {
    return [];
  }
};

const PortalTeamMembers = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [unrevokingId, setUnrevokingId] = useState(null);
  const [search, setSearch] = useState('');
  // Analytics: report that search was used at most once per mount, and never
  // the query itself (names/emails are PHI-adjacent).
  const searchTracked = useRef(false);
  const [form] = Form.useForm();
  // Fixed invite domain(s): the user types only the local part. Falls back to
  // a free email field when no domains are available client-side.
  const orgDomains = useMemo(readOrgDomains, []);
  const [emailDomain, setEmailDomain] = useState(orgDomains[0] || '');

  // Pasting a full address is common; when the pasted domain is one of the
  // org's, strip it into the fixed suffix instead of failing validation.
  const handleEmailLocalChange = (event) => {
    const value = (event?.target?.value || '');
    const atIndex = value.indexOf('@');
    if (atIndex === -1) return;
    const local = value.slice(0, atIndex);
    const domainPart = value.slice(atIndex + 1).toLowerCase();
    const matched = orgDomains.find((d) => d === domainPart || d.startsWith(domainPart));
    if (!matched) return;
    if (matched !== emailDomain) setEmailDomain(matched);
    form.setFieldsValue({ email_local: local });
  };

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(ENDPOINT);
      setMembers(sortMembers(data.team_members || []));
    } catch (error) {
      message.error(errorMessage(error, 'Failed to load team members.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleAdd = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    // With fixed domains the form collects only the local part; compose the
    // full address here. Defensively strip a matching @domain suffix in case
    // one slipped past the onChange normalization.
    let { email } = values;
    if (orgDomains.length > 0) {
      const local = (values.email_local || '').replace(new RegExp(`@${emailDomain}$`, 'i'), '');
      email = `${local}@${emailDomain}`;
    }

    setSubmitting(true);
    try {
      await axios.post(
        ENDPOINT,
        { team_member: { name: values.name, email, is_admin: values.is_admin || false } },
        { headers: csrfHeaders() },
      );
      captureAnalytics('portal_team_member_invited', { admin_granted: values.is_admin || false });
      message.success('Invitation sent. The member will receive an email to set their password.');
      setModalOpen(false);
      form.resetFields();
      fetchMembers();
    } catch (error) {
      message.error(errorMessage(error, 'Failed to add team member.'));
    } finally {
      setSubmitting(false);
    }
  };

  // Update a single row in place (preserving its position, so the load-time
  // sort doesn't reflow the list mid-edit).
  const applyMember = (updated) => {
    if (!updated) return;
    setMembers((prev) => prev.map((m) => (m.id === updated.id ? { ...m, ...updated } : m)));
  };

  const handleToggleAdmin = async (member, nextIsAdmin) => {
    try {
      const res = await axios.patch(
        `${ENDPOINT}/${member.id}`,
        { team_member: { is_admin: nextIsAdmin } },
        { headers: csrfHeaders() },
      );
      applyMember(res?.data?.team_member);
      captureAnalytics('portal_team_member_admin_toggled', { is_admin: nextIsAdmin });
    } catch (error) {
      message.error(errorMessage(error, 'Failed to update admin access.'));
    }
  };

  const handleRevoke = async (member) => {
    try {
      await axios.delete(`${ENDPOINT}/${member.id}`, { headers: csrfHeaders() });
      // No body on 204; the row stays put and just flips to revoked.
      setMembers((prev) => prev.map((m) => (
        m.id === member.id ? { ...m, is_revoked: true, is_admin: false } : m
      )));
      captureAnalytics('portal_team_member_revoked');
    } catch (error) {
      message.error(errorMessage(error, 'Failed to revoke member.'));
    }
  };

  const handleUnrevoke = async (member) => {
    setUnrevokingId(member.id);
    try {
      const res = await axios.patch(
        `${ENDPOINT}/${member.id}`,
        { team_member: { revoked: false } },
        { headers: csrfHeaders() },
      );
      applyMember(res?.data?.team_member || { id: member.id, is_revoked: false });
      captureAnalytics('portal_team_member_unrevoked');
    } catch (error) {
      message.error(errorMessage(error, 'Failed to unrevoke member.'));
    } finally {
      setUnrevokingId(null);
    }
  };

  const query = search.trim().toLowerCase();
  // Members are kept in sorted order in state (sortMembers on load); filtering
  // preserves that order.
  const visibleMembers = query
    ? members.filter((member) => [member.name, member.email]
      .some((field) => (field || '').toLowerCase().includes(query)))
    : members;

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name, member) => (
        <span>
          {name}
          {member.is_current_user && <Tag style={{ marginLeft: 8 }}>You</Tag>}
        </span>
      ),
    },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    {
      title: 'Status',
      dataIndex: 'invitation_pending',
      key: 'status',
      render: (pending, member) => {
        if (member.is_revoked) return <Tag color="red">Revoked</Tag>;
        return pending
          ? <Tag color="orange">Invite pending</Tag>
          : <Tag color="green">Active</Tag>;
      },
    },
    {
      title: 'Admin',
      dataIndex: 'is_admin',
      key: 'is_admin',
      render: (isAdmin, member) => (
        member.is_revoked ? null : (
          <Switch
            checked={isAdmin}
            disabled={member.is_current_user}
            onChange={(checked) => handleToggleAdmin(member, checked)}
          />
        )
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 100,
      render: (_, member) => {
        if (member.is_current_user) return null;
        if (member.is_revoked) {
          return (
            <button
              type="button"
              className="portal-settings__action-link portal-settings__action-link--unrevoke"
              disabled={unrevokingId === member.id}
              onClick={() => handleUnrevoke(member)}
            >
              Unrevoke
            </button>
          );
        }
        return (
          <Popconfirm
            title="Revoke this member's access? You can unrevoke them later."
            okText="Revoke"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleRevoke(member)}
          >
            {/* Row-level destructive action: a link-styled button with the
                danger emphasis carried by the confirm dialog, matching the
                app's table pattern (e.g. ManageLabels' delete). Unrevoke uses
                the same style for consistent size and alignment. */}
            <button type="button" className="portal-settings__action-link portal-settings__action-link--revoke">Revoke</button>
          </Popconfirm>
        );
      },
    },
  ];

  return (
    <div className="portal-settings__section">
      <div className="portal-settings__section-header">
        <div>
          <Typography variant="subtitleOne" bold>Team Members</Typography>
          <Typography variant="text" size="small" className="portal-settings__subtitle">
            Add or remove the people in your organization who can access the portal.
          </Typography>
        </div>
        <div className="portal-settings__section-actions">
          <Input.Search
            className="portal-settings__member-search"
            placeholder="Search by name or email"
            allowClear
            value={search}
            onChange={(event) => {
              const { value } = event.target;
              if (!searchTracked.current && value.trim()) {
                searchTracked.current = true;
                captureAnalytics('portal_team_member_search_used');
              }
              setSearch(value);
            }}
          />
          <Button
            type="primary"
            size="small"
            label="Add Member"
            leftIcon={<Plus size={16} weight="bold" />}
            onClick={() => setModalOpen(true)}
          />
        </div>
      </div>

      {loading ? (
        <div className="portal-settings__loading"><Spin /></div>
      ) : (
        <Table
          dataSource={visibleMembers}
          columns={columns}
          rowKey="id"
          rowClassName={(member) => (member.is_revoked ? 'portal-settings__row--revoked' : '')}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          locale={{
            emptyText: <Empty description={query ? 'No members match your search' : 'No team members yet'} />,
          }}
        />
      )}

      <Modal
        title="Add Team Member"
        visible={modalOpen}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        onOk={handleAdd}
        okText="Send Invite"
        confirmLoading={submitting}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Full name"
            rules={[{ required: true, message: 'Name is required' }]}
          >
            <Input placeholder="Jane Doe" />
          </Form.Item>
          {orgDomains.length > 0 ? (
            <Form.Item
              name="email_local"
              label="Email"
              rules={[
                { required: true, message: 'Email is required' },
                { pattern: /^[^@\s]+$/, message: 'Enter just the part before the @ — the domain is fixed' },
              ]}
            >
              <Input
                placeholder="jane"
                onChange={handleEmailLocalChange}
                suffix={orgDomains.length === 1 ? (
                  <span className="portal-settings__email-domain">{`@${orgDomains[0]}`}</span>
                ) : (
                  <Select
                    className="portal-settings__email-domain-select"
                    bordered={false}
                    value={emailDomain}
                    onChange={setEmailDomain}
                    options={orgDomains.map((d) => ({ value: d, label: `@${d}` }))}
                  />
                )}
              />
            </Form.Item>
          ) : (
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'Email is required' },
                { type: 'email', message: 'Enter a valid email' },
              ]}
            >
              <Input placeholder="jane@example.com" />
            </Form.Item>
          )}
          <Form.Item name="is_admin" valuePropName="checked">
            <Checkbox>Grant admin access (can manage team members and routing identifiers)</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PortalTeamMembers;
